import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Search, X, Upload, FileText, Image as ImageIcon, Star, Trash2, ChevronLeft, ChevronRight, ScanLine, MessageCircle, Mail, Check,
} from "lucide-react";
import {
  getDiagnosticDepartments,
  getDiagnosticWorklist,
  uploadDiagnosticReport,
  deleteDiagnosticReport,
  sendDiagnosticReport,
} from "../../api/api";
import { Checkbox } from "../../components/ui/checkbox";
import SegmentedControl from "../../components/SegmentedControl";
import ConfirmModal from "../../components/ConfirmModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { LAB_ORDER_STATUS_LABEL } from "../../lib/lab-constants";
import { openReport, formatFileSize } from "../../lib/reports";

const ISO = "YYYY-MM-DD";
const MAX_MB = 15;
const ACCEPT = "application/pdf,image/jpeg,image/png";

const day = (offset = 0) => moment().add(offset, "days").format(ISO);
const DATE_PRESETS = [
  { value: "any", label: "Any date", range: () => ["", ""] },
  { value: "today", label: "Today", range: () => [day(), day()] },
  { value: "tomorrow", label: "Tomorrow", range: () => [day(1), day(1)] },
  { value: "next7", label: "Next 7d", range: () => [day(), day(6)] },
  { value: "next30", label: "Next 30d", range: () => [day(), day(29)] },
  { value: "last7", label: "Last 7d", range: () => [day(-6), day()] },
  { value: "last30", label: "Last 30d", range: () => [day(-29), day()] },
];

const ageGender = (patient) => {
  const parts = [];
  if (patient?.dob) parts.push(`${moment().diff(patient.dob, "years")}y`);
  if (patient?.gender) parts.push(patient.gender[0].toUpperCase());
  return parts.join(" · ");
};

// A pure technician lands here instead of the clinic Dashboard: straight
// into their (first) department's worklist.
export function TechnicianHome() {
  const [departments, setDepartments] = useState(null);

  useEffect(() => {
    getDiagnosticDepartments()
      .then((res) => setDepartments(res.data))
      .catch(() => setDepartments([]));
  }, []);

  if (departments === null) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (departments.length) return <Navigate to={`/diagnostics/${departments[0]._id}`} replace />;
  return (
    <div className="mx-auto max-w-md rounded-lg border border-border bg-card p-6 text-center">
      <ScanLine className="mx-auto h-8 w-8 text-muted-foreground" />
      <h2 className="mt-3 font-serif text-lg font-semibold">No department assigned</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Your login isn't linked to an active diagnostic department yet. Ask your administrator to assign one in User Master.
      </p>
    </div>
  );
}

function UploadReportModal({ target, onOpenChange, onUploaded }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!target) return;
    setFile(null);
    setNote("");
    setProgress(null);
  }, [target]);

  const pick = (f) => {
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type)) {
      toast.error("Only PDF, JPG or PNG files can be uploaded.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File is too large — the limit is ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file to upload.");
    const form = new FormData();
    form.append("file", file);
    if (note.trim()) form.append("note", note.trim());
    setProgress(0);
    try {
      await uploadDiagnosticReport(target.order._id, target.item.code, form, (ev) =>
        setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0)
      );
      toast.success(`Report uploaded for ${target.order.patient?.name}.`);
      onOpenChange(false);
      onUploaded();
    } catch (err) {
      // nginx answers an oversized body with an HTML page, not our JSON.
      const tooLarge = err?.response?.status === 413 || (typeof err === "string" && err.includes("413"));
      toast.error(tooLarge ? "File is too large for the server to accept." : err?.message || "Could not upload the report.");
      setProgress(null);
    }
  };

  const uploading = progress !== null;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !uploading && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload report</DialogTitle>
        </DialogHeader>
        {target && (
          <form onSubmit={submit} className="space-y-4">
            {/* Shown back to the technician before they upload, so a report
                never lands on the wrong patient or test by accident. */}
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{target.order.patient?.name}</p>
              <p className="text-muted-foreground">
                {target.order.patient?.phone} {ageGender(target.order.patient) && `· ${ageGender(target.order.patient)}`}
              </p>
              <p className="mt-2">
                <span className="text-muted-foreground">Test:</span> {target.item.name}{" "}
                <span className="text-xs text-muted-foreground">({target.item.code})</span>
              </p>
              <p>
                <span className="text-muted-foreground">Booking:</span> {target.order.order_no} ·{" "}
                {moment(target.order.scheduled_date).format("D MMM YYYY")}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Report file *</Label>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pick(e.dataTransfer.files?.[0]);
                }}
                className="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-input px-4 py-6 text-sm text-muted-foreground hover:bg-muted/40"
              >
                <Upload className="h-5 w-5" />
                {file ? (
                  <span className="text-foreground">
                    {file.name} · {formatFileSize(file.size)}
                  </span>
                ) : (
                  <span>Click or drop a PDF, JPG or PNG (max {MAX_MB} MB)</span>
                )}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Repeat scan advised" maxLength={500} />
            </div>

            {uploading && (
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" disabled={uploading} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !file}>
                {uploading ? `Uploading… ${progress}%` : "Upload report"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SEND_LABEL = { whatsapp: "WhatsApp", whatsapp_link: "WhatsApp", email: "Email" };

const sentChannels = (reports) =>
  [...new Set(reports.flatMap((r) => (r.sends || []).filter((s) => s.status !== "failed").map((s) => SEND_LABEL[s.channel])))];

function SendEmailModal({ target, onOpenChange, onSent }) {
  const [email, setEmail] = useState("");
  const [save, setSave] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!target) return;
    setEmail(target.order.patient?.email || "");
    setSave(true);
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendDiagnosticReport(target.order._id, target.item.code, target.report._id, {
        channel: "email",
        to: email.trim(),
        save_email: save && !target.order.patient?.email,
      });
      toast.success(`Report emailed to ${email.trim()}.`);
      onOpenChange(false);
      onSent();
    } catch (err) {
      toast.error(err?.message || "Could not send the email.");
      onSent(); // the failure is logged on the report — show it
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !sending && onOpenChange(v)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Email report</DialogTitle>
        </DialogHeader>
        {target && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {target.item.name} for <span className="font-medium text-foreground">{target.order.patient?.name}</span> — the
              file is sent as an attachment.
            </p>
            <div className="space-y-1.5">
              <Label>Email address *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="patient@example.com" required />
            </div>
            {!target.order.patient?.email && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={save} onCheckedChange={(v) => setSave(!!v)} />
                Save this email on the patient's record
              </label>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" disabled={sending} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending || !email.trim()}>
                {sending ? "Sending…" : "Send email"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportChip({ report, onDelete, onWhatsApp, onEmail }) {
  const Icon = report.mime_type === "application/pdf" ? FileText : ImageIcon;
  const open = () => openReport(report).catch((err) => toast.error(err?.message || "Could not open the report."));
  const lastSends = Object.values(
    (report.sends || []).reduce((acc, s) => ({ ...acc, [SEND_LABEL[s.channel]]: s }), {})
  );
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-card pl-2 text-xs">
          <button type="button" onClick={open} className="flex min-w-0 items-center gap-1 py-1 hover:underline" title={report.note || report.original_name}>
            <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="truncate">{report.original_name}</span>
          </button>
          <span className="shrink-0 text-muted-foreground">
            · {report.uploaded_by_name}, {moment(report.uploaded_at).format("D MMM h:mm A")}
          </span>
          <button
            type="button"
            onClick={onDelete}
            className="shrink-0 rounded-r-md px-1.5 py-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            aria-label={`Remove ${report.original_name}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </span>
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={onWhatsApp}>
          <MessageCircle className="h-3.5 w-3.5 text-green-600" /> WhatsApp
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={onEmail}>
          <Mail className="h-3.5 w-3.5" /> Email
        </Button>
      </div>
      {lastSends.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {lastSends.map((s) => (
            <span
              key={s.channel}
              title={s.error || undefined}
              className={
                s.status === "failed"
                  ? "inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
                  : "inline-flex items-center gap-1 rounded-full bg-green-600/10 px-2 py-0.5 text-xs font-medium text-green-700"
              }
            >
              {s.status === "failed" ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              {s.status === "failed" ? `${SEND_LABEL[s.channel]} failed` : `Sent on ${SEND_LABEL[s.channel]}`}
              <span className="font-normal opacity-80">
                · {moment(s.at).format("D MMM h:mm A")}
                {s.to ? ` · ${s.to}` : ""}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onUpload, onDelete, onWhatsApp, onEmail }) {
  const p = order.patient;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium">
            {order.priority && <Star className="h-4 w-4 fill-warning text-warning" aria-label="Priority" />}
            {p?.name || "Unknown patient"}
            {ageGender(p) && <span className="text-sm font-normal text-muted-foreground">{ageGender(p)}</span>}
          </p>
          <p className="text-sm text-muted-foreground">{p?.phone}</p>
        </div>
        <div className="text-right text-sm">
          <p className="font-medium tabular-nums">{order.order_no}</p>
          <p className="text-muted-foreground">
            {moment(order.scheduled_date).format("D MMM YYYY")} · {order.scheduled_slot}
          </p>
        </div>
      </div>

      {(order.referred_by || order.notes || order.other_tests > 0) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {[
            order.referred_by && `Referred by ${order.referred_by}`,
            order.notes,
            order.other_tests > 0 && `+${order.other_tests} test${order.other_tests === 1 ? "" : "s"} in other departments`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      <ul className="mt-3 divide-y divide-border rounded-md border border-border">
        {order.items.map((item) => (
          <li key={item.code} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1.5">
              <p className="text-sm font-medium">
                {item.name} <span className="text-xs font-normal text-muted-foreground">{item.code}</span>
              </p>
              {item.reports.length > 0 ? (
                <div className="space-y-2">
                  {item.reports.map((r) => (
                    <ReportChip
                      key={r._id}
                      report={r}
                      onDelete={() => onDelete(order, item, r)}
                      onWhatsApp={() => onWhatsApp(order, item, r)}
                      onEmail={() => onEmail(order, item, r)}
                    />
                  ))}
                </div>
              ) : item.reported_at ? (
                <p className="text-xs text-muted-foreground">Marked delivered by the desk — no file uploaded</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {sentChannels(item.reports).length > 0 ? (
                <Badge className="gap-1 bg-green-600 text-white hover:bg-green-600">
                  <Check className="h-3 w-3" /> Sent
                </Badge>
              ) : (
                <Badge variant={item.reported_at ? "secondary" : "warning"}>{item.reported_at ? "Reported" : "Pending"}</Badge>
              )}
              <Button size="sm" variant={item.reports.length ? "outline" : "default"} onClick={() => onUpload(order, item)}>
                <Upload className="h-3.5 w-3.5" /> {item.reports.length ? "Add file" : "Upload report"}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {order.status && (
        <p className="mt-2 text-right text-xs text-muted-foreground">Booking status: {LAB_ORDER_STATUS_LABEL[order.status] || order.status}</p>
      )}
    </div>
  );
}

export default function DiagnosticWorklist() {
  const { departmentId } = useParams();
  const [department, setDepartment] = useState(null);
  const [status, setStatus] = useState("pending");
  // Pending means "still owed a report", whatever day it was booked for —
  // so it starts unfiltered (oldest first); the other tabs start on today.
  const [preset, setPreset] = useState("any");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1, counts: {} });
  const [loading, setLoading] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);

  // Through AiSensy once the report template is approved (the file arrives
  // in WhatsApp); until then the backend returns a wa.me link and staff
  // send the secure report link from their own WhatsApp. The tab is opened
  // up front so the browser doesn't block it as a popup.
  const sendWhatsApp = async (order, item, report) => {
    const tab = window.open("", "_blank");
    try {
      const res = await sendDiagnosticReport(order._id, item.code, report._id, { channel: "whatsapp" });
      if (res.data.whatsapp_url) {
        if (tab) tab.location.href = res.data.whatsapp_url;
        else window.location.href = res.data.whatsapp_url;
      } else {
        tab?.close();
        toast.success(`Report sent to ${order.patient?.name} on WhatsApp.`);
      }
      load();
    } catch (err) {
      tab?.close();
      toast.error(err?.message || "Could not send on WhatsApp.");
      load();
    }
  };

  useEffect(() => {
    setPage(1);
    getDiagnosticDepartments()
      .then((res) => setDepartment(res.data.find((d) => d._id === departmentId) || null))
      .catch(() => setDepartment(null));
  }, [departmentId]);

  const load = useCallback(() => {
    setLoading(true);
    const [from, to] = DATE_PRESETS.find((p) => p.value === preset).range();
    getDiagnosticWorklist({
      department_id: departmentId,
      status,
      from: from || undefined,
      to: to || undefined,
      search: search || undefined,
      page,
      limit: 20,
    })
      .then((res) => {
        setOrders(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage, counts: res.counts });
      })
      .catch((err) => toast.error(err?.message || "Could not load the worklist."))
      .finally(() => setLoading(false));
  }, [departmentId, status, preset, search, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const confirmDelete = async () => {
    const { order, item, report } = deleteTarget;
    try {
      await deleteDiagnosticReport(order._id, item.code, report._id);
      toast.success("Report removed.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Could not remove the report.");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
          <ScanLine className="h-5 w-5" /> {department?.name || "Diagnostics"}
        </h2>
        <p className="text-sm text-muted-foreground">
          Bookings with {department?.name || "this department"}'s tests — upload each report against its test once it's done
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          items={[
            { value: "pending", label: "Pending", count: meta.counts.pending },
            { value: "reported", label: "Reported", count: meta.counts.reported },
            { value: "all", label: "All" },
          ]}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPreset(v === "pending" ? "any" : "today");
            setPage(1);
          }}
        />
        <SegmentedControl
          size="sm"
          items={DATE_PRESETS}
          value={preset}
          onChange={(v) => {
            setPreset(v);
            setPage(1);
          }}
        />
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Patient, phone or order no…"
            className="h-9 pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {loading && orders.length === 0 ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {status === "pending" ? "Nothing pending — every booking in this range is reported." : "No bookings match these filters."}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderCard
              key={o._id}
              order={o}
              onUpload={(order, item) => setUploadTarget({ order, item })}
              onDelete={(order, item, report) => setDeleteTarget({ order, item, report })}
              onWhatsApp={sendWhatsApp}
              onEmail={(order, item, report) => setEmailTarget({ order, item, report })}
            />
          ))}
        </div>
      )}

      {meta.numberOfPage > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-muted-foreground">
            Page {page} of {meta.numberOfPage}
          </span>
          <Button size="sm" variant="outline" disabled={page >= meta.numberOfPage} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <UploadReportModal target={uploadTarget} onOpenChange={(v) => !v && setUploadTarget(null)} onUploaded={load} />

      <SendEmailModal target={emailTarget} onOpenChange={(v) => !v && setEmailTarget(null)} onSent={load} />

      <ConfirmModal
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Remove this report?"
        description={
          deleteTarget &&
          `"${deleteTarget.report.original_name}" will be removed from ${deleteTarget.order.patient?.name}'s ${deleteTarget.item.name}. Use this only for a wrong or duplicate upload.`
        }
        confirmLabel="Remove report"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
